import angular from 'angular';
import { hasOwnProperty } from '../../functions/hasOwnProperty';
export const REGISTERED_AXES = ['ital', 'opsz', 'slnt', 'wght', 'wdth'];
export type VFRegisteredAxes = 'wght' | 'wdth' | 'slnt' | 'opsz' | 'ital';
export type VFCustomAxes = VFRegisteredAxes | string;
export type VFAxisList = VFCustomAxes[];
export type VariationSettings = Record<VFCustomAxes, number>;

export interface IVFAxisDefinition {
    name: string;
    min: number;
    max: number;
    default: number;
}

export interface IVFInstance {
    axes: VariationSettings;
    name: string;
}

export interface IVFInfo {
    axes: Record<VFCustomAxes, IVFAxisDefinition>;
    order: VFAxisList;
    instances: IVFInstance[];
}

export function isIVFAxisDefinition(obj: unknown): obj is IVFAxisDefinition {
    return (
        hasOwnProperty(obj, 'name') &&
        angular.isString(obj.name) &&
        hasOwnProperty(obj, 'min') &&
        angular.isNumber(obj.min) &&
        hasOwnProperty(obj, 'max') &&
        angular.isNumber(obj.max) &&
        hasOwnProperty(obj, 'default') &&
        angular.isNumber(obj.default)
    );
}

export class VariableInfo implements IVFInfo {
    protected _instances: IVFInstance[] = [];
    instanceIdxByAxesHash: Record<string, IVFInstance>;

    // we added default instance to support out selects and labels...
    get instances(): IVFInstance[] {
        return this._instances;
    }

    get order(): VFCustomAxes[] {
        return this.info.order;
    }

    get axes(): Record<string, IVFAxisDefinition> {
        return this.info.axes;
    }

    constructor(protected info: IVFInfo) {
        // console.log('variable_info', info);
        // generate a default instance for use in selects.
        const defaultAxes = this.info.order.reduce<VariationSettings>(
            (axes, axis) => {
                if (!hasOwnProperty(this.axes, axis)) {
                    // it's okay to have an axis in the order that isn't in the axes list. this is within spec and we should tolerate it, but not add it to our defaults list.
                    return axes;
                }
                if (!hasOwnProperty(this.axes[axis], 'default')) {
                    console.error(
                        `Please contact suppport@typenetwork.com and let us know the default value for the '${axis}' is missing. Please include the following information: `,
                        { location: window.location.href, axis, info }
                    );
                    return axes;
                }
                axes[axis] = this.axes[axis].default;
                return axes;
            },
            {}
        );
        const defaultInstance = {
            name: 'Default',
            axes: defaultAxes,
        };

        const defaultInstanceHash = this.getAxesHash(defaultAxes);

        // generate an index of instances by axes values on construction
        // so we can quickly reverse map from axes to an instance without needing
        // to constantly iterate the axes.
        this.instanceIdxByAxesHash = this.info.instances.reduce(
            (idx, instance) => {
                const key = this.getAxesHash(instance.axes);
                idx[key] = instance;
                return idx;
            },
            {}
        );
        this._instances = info.instances;

        // add the default option, as a shortcut to get to the default named instance.
        // but don't add it twice. it was duplicating at one point in time.
        if (this._instances[0]?.name != 'Default') {
            this._instances.unshift(defaultInstance);
        }

        if (!this.instanceIdxByAxesHash[defaultInstanceHash]) {
            // only add default to the lookup hash if there isn't already a named instance that matches the default.
            this.instanceIdxByAxesHash[defaultInstanceHash] = defaultInstance;
        }
    }

    getAxesHash(axes: Record<VFCustomAxes, number>): string {
        // use the axes values of an instance concatenated in axis order
        // to create a consistent lookup hash to reverse map instances.
        const orderedAxisValues = this.info.order.map((axis) => axes[axis]);
        const key = orderedAxisValues.join(',');
        return key;
    }

    getInstanceByAxes(
        axes: Record<VFCustomAxes, number>
    ): IVFInstance | undefined {
        const instanceKey = this.getAxesHash(axes);
        const instance = this.instanceIdxByAxesHash[instanceKey] || undefined;
        // console.log('getInstanceByAxes', instanceKey, instance, this.instanceIdxByAxesHash);
        return instance;
    }
}
